import { blupayApi, getApiError } from "../api/client";
import { tokenService } from "../api/token.service";

/**
 * authService
 *
 * Matches the documented /api/v1/auth/* endpoints (Swagger). A couple of
 * notes on choices made here vs. the original draft of this file:
 *
 * - /auth/refresh takes { refresh_token } in the JSON body, per Swagger —
 *   NOT a Bearer header. (Worth double-checking against a live call if the
 *   backend ever 422s here; the doc is the source of truth for now.)
 * - Signup returns a pending email challenge. Only /auth/verify-email stores
 *   the first token pair after the server accepts the one-time code.
 * - logout is now async and revokes the session server-side via
 *   POST /auth/logout with { refresh_token }, then clears local tokens.
 *   Old behavior (local-only clear) is still useful as a fallback if the
 *   server call fails — see logout() below, it clears tokens either way.
 */
export const authService = {
  signup: async (payload) => {
    try {
      const res = await blupayApi.post("/auth/register", payload);

      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  verifyEmail: async (payload) => {
    try {
      const res = await blupayApi.post("/auth/verify-email", payload);

      if (res.data?.access_token && res.data?.refresh_token) {
        tokenService.setTokens(res.data.access_token, res.data.refresh_token);
      }

      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  resendEmailOtp: async (verificationId) => {
    try {
      const res = await blupayApi.post("/auth/resend-email-otp", {
        verification_id: verificationId,
      });
      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  login: async (payload) => {
    try {
      const res = await blupayApi.post("/auth/login", payload);

      if (res.data?.access_token && res.data?.refresh_token) {
        tokenService.setTokens(res.data.access_token, res.data.refresh_token);
      }

      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  refreshToken: async () => {
    try {
      const refreshToken = tokenService.getRefreshToken();

      const res = await blupayApi.post("/auth/refresh", {
        refresh_token: refreshToken,
      });

      if (res.data?.access_token && res.data?.refresh_token) {
        tokenService.setTokens(res.data.access_token, res.data.refresh_token);
      }

      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  /** Returns the authenticated user from the current access token. */
  getMe: async () => {
    try {
      const res = await blupayApi.get("/auth/me");
      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  /**
   * Changes password. Backend revokes all refresh sessions on success —
   * caller should treat this as a forced logout (tokens won't work after).
   */
  changePassword: async (payload) => {
    try {
      const res = await blupayApi.post("/auth/change-password", payload);
      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  /**
   * Request a password-reset code by email. The response is intentionally
   * generic and never reveals whether an account exists for the address.
   */
  forgotPassword: async ({ email }) => {
    try {
      const res = await blupayApi.post("/auth/password/forgot", { email });
      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  /** Reset the password using the emailed code. Revokes all sessions. */
  resetPassword: async ({ email, code, newPassword }) => {
    try {
      const res = await blupayApi.post("/auth/password/reset", {
        email,
        code,
        new_password: newPassword,
      });
      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  /**
   * Logs out the current device: revokes this refresh-token session
   * server-side, then clears local tokens regardless of whether the
   * server call succeeds (the token may already be expired/invalid —
   * the user's intent to log out should still be honored locally).
   */
  logout: async () => {
    const refreshToken = tokenService.getRefreshToken();
    try {
      if (refreshToken) {
        await blupayApi.post("/auth/logout", { refresh_token: refreshToken });
      }
    } catch {
      // Swallow — session may already be dead server-side; local logout
      // still needs to happen.
    } finally {
      tokenService.clearTokens();
    }
  },

  /** Revokes every active session for this user, everywhere. */
  logoutAll: async () => {
    try {
      await blupayApi.post("/auth/logout-all");
    } finally {
      tokenService.clearTokens();
    }
  },

  getPinStatus: async () => {
    try {
      const res = await blupayApi.get("/auth/pin/status");
      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  setupPin: async (payload) => {
    // payload: { current_password, pin }
    try {
      const res = await blupayApi.post("/auth/pin/setup", payload);
      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  changePin: async (payload) => {
    // payload: { current_pin, new_pin }
    try {
      const res = await blupayApi.post("/auth/pin/change", payload);
      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },

  /**
   * Verifies the transaction PIN for a given purpose (e.g. "transaction").
   * Does not mutate auth state — callers use the boolean `verified` result
   * to gate whatever action required the PIN (e.g. submitting a transfer).
   */
  verifyPin: async (payload) => {
    // payload: { pin, purpose }
    try {
      const res = await blupayApi.post("/auth/pin/verify", payload);
      return res.data;
    } catch (error) {
      throw getApiError(error);
    }
  },
};
